import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Spinner,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import ProjectedLineup from './ProjectedLineup';
import { getTeamInfoByAbbreviation } from '../utils/getTeamInfo';
import { usePotentialLineup } from '../hooks/useLineups';
import { buildLineupsFromPotentialResponse } from '../utils/projectedLineupUtils';

export interface ProjectedLineupModalGame {
  awayAbbr: string;
  homeAbbr: string;
  dateLabel: string;
  awayTeamId?: number;
  homeTeamId?: number;
  leagueId?: number;
  gameDate?: string;
}

/** Matches getTeamInfoByAbbreviation second argument (populated league). */
type LeagueForModal = Parameters<typeof getTeamInfoByAbbreviation>[1];

interface ProjectedLineupModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: ProjectedLineupModalGame | null;
  league: LeagueForModal;
}

/**
 * Modal that shows the potential lineup for a game.
 * Title: "{away} at {home} - {date}".
 * Content: ProjectedLineup wired to usePotentialLineup (YTD vs Last 7 days, team-colored panels).
 *
 * Data mapping is shared with dashboard panels via ../utils/projectedLineupUtils (keep in sync with admin).
 */
const ProjectedLineupModal: React.FC<ProjectedLineupModalProps> = ({ isOpen, onClose, game, league }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const awayInfo = game ? getTeamInfoByAbbreviation(game.awayAbbr, league) : null;
  const homeInfo = game ? getTeamInfoByAbbreviation(game.homeAbbr, league) : null;

  const { data, isLoading } = usePotentialLineup(
    game?.awayTeamId,
    game?.homeTeamId,
    game?.leagueId,
    game?.gameDate,
    {
      enabled: !!game?.awayTeamId && !!game?.homeTeamId,
    }
  );

  const { awayLineup, homeLineup, awayStarter, homeStarter } = buildLineupsFromPotentialResponse(data ?? undefined);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          Projected Lineup: {game ? `${game.awayAbbr} at ${game.homeAbbr} - ${game.dateLabel}` : ''} - 2pm PST
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {!game ? (
            <Box />
          ) : isLoading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="lg" />
            </Flex>
          ) : (
            <>
              <ProjectedLineup
                awayAbbr={game.awayAbbr}
                homeAbbr={game.homeAbbr}
                awayTeamColor={awayInfo?.color_primary}
                homeTeamColor={homeInfo?.color_primary}
                awayTeamInfo={awayInfo ?? undefined}
                homeTeamInfo={homeInfo ?? undefined}
                awayLineup={awayLineup}
                homeLineup={homeLineup}
                awayStarter={awayStarter || undefined}
                homeStarter={homeStarter || undefined}
              />
              <Text mt={3} fontSize="sm" fontStyle="italic" opacity={0.9}>
                * Stats are YTD Dominus Game Stats
              </Text>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProjectedLineupModal;
